import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingService } from '../../services/loading.service';
import { FileUtilService } from '../../services/file-util.service';
import { TrainingAnonymousService } from '../../services/training-anonymous.service';
import { TrainingAnonymousModel } from '../../models/training/training-anonymous.model';
import { TrainingLocationTypeModel } from '../../models/training/training-location-type.model';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { DomSanitizer } from '@angular/platform-browser';
import { calendarIcon } from '../../app-icons';
import { TrainerModel } from 'src/app/models/trainer.model';
import { EventMemberStatusModel } from 'src/app/models/events/event-member-status.model';
import { forkJoin } from 'rxjs';
import { RegistrationFieldConfigModel } from 'src/app/models/registration-field-configs/registration-field-config.model';

@Component({
  selector: 'app-my-invitation',
  templateUrl: './my-invitation.component.html',
  styleUrls: ['./my-invitation.component.scss'],
  standalone: false
})
export class MyInvitationComponent {
  trainingKey: string;
  accessToken: string;
  channelToken: string | null = null;
  TrainingLocationType = TrainingLocationTypeModel;
  training: TrainingAnonymousModel | null = null;
  showTokenAcceptFlow: boolean = false;
  showAcceptInvitationModal: boolean = false;
  showDeclineInvitationModal: boolean = false;
  isInvitationAccepted: boolean = false;
  isInvitationDeclined: boolean = false;
  location: { latitude: number; longitude: number; location: string } | null = null;
  trainers: TrainerModel[] = [];
  registrationFieldConfigs: RegistrationFieldConfigModel[] = [];
  
  get locationType() {
    switch (this.training?.locationType) {
      case TrainingLocationTypeModel.Address:
        return this.translateService.instant(
          'COMMON.FIELDS.EVENT-TYPES.FACE-TO-FACE'
        );
      case TrainingLocationTypeModel.Web:
        return this.translateService.instant(
          'COMMON.FIELDS.EVENT-TYPES.ONLINE'
        );
      case TrainingLocationTypeModel.GoToWebinar:
        return 'GoToWebinar';
      case TrainingLocationTypeModel.Webex:
        return 'Webex';
      case TrainingLocationTypeModel.MsTeamsWebinar:
        return 'MsTeams';
      case TrainingLocationTypeModel.MsTeamsMeetings:
        return 'MsTeamsMeetings';
      default:
        return '';
    }
  }

  get durationString() {
    if (
      !this.training ||
      typeof this.training?.iddTime !== 'number' ||
      isNaN(this.training.iddTime)
    ) {
      return '00:00 h';
    }

    const hours = Math.floor(this.training?.iddTime / 60);
    const remainingMinutes = this.training?.iddTime % 60;
    const durationString = `${hours}:${
      remainingMinutes < 10 ? '0' : ''
    }${remainingMinutes} h`;

    return durationString;
  }

  get locationDisplay(): { text: string; url?: string } | null {
    if (!this.training) return null;

    const { locationType, locationSuffix, joinUrl } = this.training;

    const suffix = locationSuffix?.trim();
    const join = joinUrl?.trim();

    const hasSuffix = !!suffix;
    const hasJoinUrl = !!join;

    const isAddress = locationType === TrainingLocationTypeModel.Address;
    const isWeb = locationType === TrainingLocationTypeModel.Web;
    const isExternal = locationType === TrainingLocationTypeModel.ExternalEvent;
    const isOther = !isAddress && !isWeb && !isExternal;

    const asLink = (value: string) =>
      value.startsWith('https://')
        ? { text: value, url: value }
        : { text: value };

    if (isAddress) {
      return hasSuffix 
        ? { text: suffix }
        : null;
    }

    if(
      isExternal &&
      hasSuffix
    ) {
      return asLink(suffix);
    }

    if (
      isWeb &&
      hasSuffix
    ) {
      if (this.isInvitationAccepted) {
        return asLink(suffix);
      } else {
        return {
          text: this.translateService.instant(
            "MY-INVITATION.ACCESS-LINK-INFO2"
          )
        };
      }
    }

    if (
      isOther && 
      !this.accessToken &&
      hasSuffix
    ) {
      if(this.isInvitationAccepted) {
        return asLink(suffix);
      } else {
        return {
          text: this.translateService.instant(
            "MY-INVITATION.ACCESS-LINK-INFO"
          )
        };
      }
    }

    if (
      isOther &&
      this.accessToken
    ) {
      if(hasJoinUrl) {
        return asLink(join);
      } else {
        return {
          text: this.translateService.instant(
            "MY-INVITATION.ACCESS-LINK-INFO"
          )
        };
      }
    }

    return null;
  }

  get isExternalEvent() {
    return this.training?.locationType == 
      TrainingLocationTypeModel.ExternalEvent
  }

  MemberStatus = EventMemberStatusModel;

  constructor(
    private route: ActivatedRoute,
    private trainingAnonymousService: TrainingAnonymousService,
    private loadingService: LoadingService,
    public translateService: TranslateService,
    private cookieService: CookieService,
    private toastrService: ToastrService,
    private fileUtilService: FileUtilService,
    private sanitized: DomSanitizer,
    private router: Router
  ) {
    this.trainingKey = this.route.snapshot.params['key'];
    this.accessToken = this.route.snapshot.params['token'];

    this.route.queryParamMap.subscribe(params => {
      this.channelToken = params.get('channel');
    });

    this.loadTrainingData(this.accessToken);
  }

  getCalendarIcon() {
    return this.sanitized.bypassSecurityTrustHtml(calendarIcon);
  }

  loadTrainingData(accessToken: string | null = null): void {
    this.loadingService
      .load(
        forkJoin({
          training: this.trainingAnonymousService.getTrainingAnonymous(this.trainingKey, accessToken),
          registrationFieldConfigs: this.trainingAnonymousService.getRegistrationFieldConfigs(this.trainingKey)
        })
      )
      .subscribe(({training, registrationFieldConfigs}) => {
        this.training = training;
        this.registrationFieldConfigs = registrationFieldConfigs;

        if (training.latitude && training.longitude) {
          this.location = {
            latitude: training.latitude,
            longitude: training.longitude,
            location: training.location
          };
        };

        const trainingCookieValue = this.cookieService.get(
          `training-${this.trainingKey}`
        );

        if (trainingCookieValue === 'true') {
          this.isInvitationAccepted = true;
        };

        if (this.training.memberStatus == EventMemberStatusModel.Accepted) {
          this.isInvitationAccepted = true;
        } else if (this.training.memberStatus == EventMemberStatusModel.Declined) {
          this.isInvitationDeclined = true;
        };

        const admin = this.training.admin;
        this.trainers = [
          admin,
          ...this.training.trainers.filter(t => t.id !== admin.id)
        ]
      });
  }

  goToExternalEvent(): void {
    const eventUrl = this.training?.locationSuffix;

    if (!eventUrl) {
      return;
    }

    const url = eventUrl.startsWith('http')
      ? eventUrl
      : `https://${eventUrl}`;

    window.open(url, '_blank');
  }


  onInviteAccepted(): void {
    const expirationDate = new Date();
    expirationDate.setFullYear(expirationDate.getFullYear() + 1);

    this.cookieService.set(
      `training-${this.trainingKey}`,
      'true',
      expirationDate,
      '/'
    );
    this.isInvitationAccepted = true;
    this.isInvitationDeclined = false;

    if (this.accessToken) {
      this.loadTrainingData(this.accessToken);
    }
  }

  onAddedToQueue(): void {
    if (this.accessToken) {
      this.loadTrainingData(this.accessToken);
    }
  }

  onAddToQueueClick(): void {
    if(this.accessToken) {
      this.showTokenAcceptFlow = true;
    } else {
      this.showAcceptInvitationModal = true;
    }
  }

  onRemoveFromQueueClick(): void {
    this.loadingService
      .load(
        this.trainingAnonymousService.dequeueTrainingAnonymous(this.trainingKey, this.accessToken)
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translateService.instant('MY-TRAININGS.TRAINING-QUEUE.REMOVED-FROM-QUEUE-SUCCESSFULLY')
          );
          this.router.navigate(['my-invitation', this.trainingKey]);
        },
        error: () => {
          this.toastrService.error(
            this.translateService.instant('COMMON.AN-ERROR-HAS-OCCURRED')
          );
        }
      });
  }

  onAcceptInvitationClick(): void {
    if(this.accessToken) {
      this.showTokenAcceptFlow = true;
    } else {
      this.showAcceptInvitationModal = true;
    }
  }

  onDeclineInvitationClick(): void {
    this.showDeclineInvitationModal = true;
  }

  onInviteDeclined(): void {
    this.isInvitationDeclined = true;
    this.loadTrainingData(this.accessToken);
  }

  onDeclineTrainingClick(): void {
    this.loadingService
      .load(
        this.trainingAnonymousService.declineTraining(
          this.trainingKey, this.accessToken
        )
      )
      .subscribe(() => {
        this.cookieService.delete(`training-${this.trainingKey}`, '/');
        this.isInvitationAccepted = false;

        this.loadTrainingData(this.accessToken);
      });
  }

  onDownloadIcs(): void {
    this.loadingService
      .load(
        this.trainingAnonymousService.getTrainingAnonymousIcs(this.trainingKey, this.accessToken)
      )
      .subscribe({
        next: (ics) => this.fileUtilService.downloadFile(ics),
        error: () =>
          this.toastrService.error(
            this.translateService.instant('COMMON.AN-ERROR-HAS-OCCURRED')
          ),
      });
  }
}
